import { action, observable, computed, runInAction } from "mobx";
import {
	ResultsMenuCategory,
	ResultsMenuSport,
	ResultsMenuTournament,
} from "@data-types/results";
import ResultsService from "@services/results/ResultsService";
import SearchBarViewStore from "@state/stores/common/SearchBarViewStore";
import type { RootStore } from "@state/stores/types";
import { ResultsDataStore } from "@state/stores/results";

export default class ResultsMenuStore {
	rootStore: RootStore;
	resultsDataStore: ResultsDataStore;
	searchBarStore: SearchBarViewStore;

	defaultDaysFromToday = -7;

	@observable resultMenu: ResultsMenuSport[] | null;
	@observable selectedTournaments: ResultsMenuTournament[] = [];
	@observable selectedSportId: string | null = null;
	@observable expandedCategories: Set<string> = new Set();
	@observable isMenuExpanded = false;

	@computed get selectedSportObj(): ResultsMenuSport | null {
		return (
			(this.selectedSportId &&
				this.resultMenu?.find((s) => s.id === this.selectedSportId)) ||
			null
		);
	}

	@computed get selectedTournamentIds(): Set<string> {
		return new Set(this.selectedTournaments.map((t) => t.id));
	}

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.resultsDataStore = new ResultsDataStore(this);
		this.searchBarStore = new SearchBarViewStore();
	}

	isSportSelected(sportId: string) {
		return this.selectedTournaments.some((t) => t.sportId === sportId);
	}

	isTournamentSelected(tournamentId: string) {
		return this.selectedTournaments.some((t) => t.id === tournamentId);
	}

	getCategoryCheckedState(category: ResultsMenuCategory) {
		let areSomeSelected = false;
		let areAllSelected = category.children.length > 0;

		for (const catTournament of category.children) {
			const isSelected = this.selectedTournamentIds.has(catTournament.id);

			areSomeSelected = areSomeSelected || isSelected;
			areAllSelected = areAllSelected && isSelected;
		}

		if (areAllSelected) return "all";

		if (areSomeSelected) return "some";

		return null;
	}

	updateDayParam() {
		const dayParam =
			this.resultsDataStore.searchPhrase ||
			this.selectedTournaments.length
				? this.defaultDaysFromToday
				: 0;
		this.resultsDataStore.selectDay(dayParam);
	}

	@action.bound
	async onInitialize(): Promise<void> {
		const resultMenuRequest = await ResultsService.getMenu(
			this.defaultDaysFromToday
		);

		if (resultMenuRequest.success) {
			runInAction(() => {
				this.resultMenu = resultMenuRequest.data;
			});
		}
	}

	@action.bound
	toggleTournament(tournament: ResultsMenuTournament): boolean {
		if (this.resultsDataStore.hasPendingRequest) {
			return false;
		}

		const selectedTournamentIdx = this.selectedTournaments.findIndex(
			(t) => t.id === tournament.id
		);
		if (selectedTournamentIdx > -1) {
			this.selectedTournaments = [
				...this.selectedTournaments.slice(0, selectedTournamentIdx),
				...this.selectedTournaments.slice(selectedTournamentIdx + 1),
			];
		} else {
			this.selectedTournaments = [
				tournament,
				...this.selectedTournaments,
			];
		}

		if (this.resultsDataStore.searchPhrase) {
			this.resultsDataStore.clearSearchPhrase();
		} else {
			this.updateDayParam();
		}

		return true;
	}

	@action.bound
	removeTournament(tournamentId: string) {
		if (
			!this.resultsDataStore.hasPendingRequest &&
			this.selectedTournamentIds.has(tournamentId)
		) {
			this.selectedTournaments = this.selectedTournaments.filter(
				(t) => t.id !== tournamentId
			);
			this.updateDayParam();
		}
	}

	@action.bound
	toggleCategory(category: ResultsMenuCategory, isChecked: boolean) {
		if (this.resultsDataStore.hasPendingRequest) {
			return false;
		}

		const categoryTournamentIds = new Set(
			category.children.map((t) => t.id)
		);
		this.selectedTournaments = this.selectedTournaments.filter(
			(t) => !categoryTournamentIds.has(t.id)
		);

		if (isChecked) {
			this.selectedTournaments = [
				...category.children,
				...this.selectedTournaments,
			];
		}

		if (this.resultsDataStore.searchPhrase) {
			this.resultsDataStore.clearSearchPhrase();
		} else {
			this.updateDayParam();
		}

		return true;
	}

	@action.bound
	setExpandedCategories(categoryId: string): void {
		if (this.expandedCategories.has(categoryId)) {
			this.expandedCategories.delete(categoryId);
		} else {
			this.expandedCategories.add(categoryId);
		}
	}

	@action.bound
	toggleMenuExpanded(isManualToggle = false): void {
		if (isManualToggle && this.resultMenu) {
			const firstSportId =
				this.resultMenu.length > 0 ? this.resultMenu[0].id : null;

			if (firstSportId && this.selectedSportId === null) {
				this.selectedSportId = firstSportId;
			}
		}
		this.isMenuExpanded = !this.isMenuExpanded;
	}

	@action.bound
	setSelectedSport(sport: ResultsMenuSport | null): void {
		this.selectedSportId = sport?.id || null;
	}

	@action.bound
	resetSelectedTournaments(): void {
		this.selectedTournaments = [];
	}

	@action.bound
	onDispose() {
		this.resultsDataStore.onDispose();
	}
}
